import { computeSurvivor } from './survivor.js';
import { assertBalanced, computePlacements, totalPot } from './rules.js';
import { AWARD_RESOLVERS, assertResolvable } from './awards/resolvers.js';
/**
 * Apply one award's ruling to the stored map, leaving every other award alone.
 *
 * An empty list removes the key rather than storing an empty array, so "nobody
 * is ruled out" and "this award has no rulings" are the same document.
 */
export function applyEligibilityPatch(ineligible, patch) {
    const next = { ...(ineligible ?? {}) };
    if (patch.ownerIds.length > 0)
        next[patch.awardId] = patch.ownerIds;
    else
        delete next[patch.awardId];
    return next;
}
/**
 * Read an override document in either shape.
 *
 * Eligibility rulings used to be a bare list, because the First Round Clown was
 * the only award anyone could be ruled out of. Rather than migrate every stored
 * document — and risk a half-migrated collection paying the wrong manager — the
 * old field is folded into the new one on the way in, here, once.
 *
 * A document carrying both wins with the new field: otherwise a stale legacy
 * list would quietly reassert itself over a ruling the commissioner has since
 * changed. The legacy key is dropped from the result for the same reason, so
 * nothing downstream can read it by accident.
 */
export function normalizeLedgerOverrides(raw = {}) {
    const { clownIneligible, ...rest } = raw;
    if (rest.ineligible)
        return rest;
    if (!clownIneligible?.length)
        return rest;
    return { ...rest, ineligible: { firstRoundClown: clownIneligible } };
}
// Money is tracked in whole cents. Floats accumulate error across a season of
// awards, and this ledger is settled in cash between twelve people.
const dollarsToCents = (d) => Math.round(d * 100);
const centsToDollars = (c) => c / 100;
/**
 * Split an award between tied winners without losing or inventing money.
 *
 * Rounding each share independently does not conserve the pot: $25 three ways
 * at one decimal pays $8.30 x 3 = $24.90, while four ways pays $6.30 x 4 =
 * $25.20. The board would then disagree with itself about what it paid.
 *
 * So split in whole cents and hand the leftover cents out one at a time. Ties
 * are broken by owner id purely so the result is deterministic — the same tie
 * always pays the same people, rather than shifting when ESPN reorders teams.
 *
 * Returns owner id -> dollars, and always sums to exactly `amount`. Exported so
 * a manager's page can show what THEY banked from a tied award rather than the
 * pot, and show the same figure their wallet was built from — a naive divide
 * would disagree by a cent on a three-way split.
 */
export function splitAmong(amount, winners) {
    const out = new Map();
    if (winners.length === 0)
        return out;
    const totalCents = Math.round(amount * 100);
    const base = Math.floor(totalCents / winners.length);
    let leftover = totalCents - base * winners.length;
    for (const id of [...winners].sort()) {
        const cents = base + (leftover > 0 ? 1 : 0);
        if (leftover > 0)
            leftover--;
        out.set(id, cents / 100);
    }
    return out;
}
/**
 * Assemble the full money board.
 *
 * Every award reports a `status`: awards decided by season-long totals stay
 * 'live' until the regular season ends, because a single big week can flip
 * them. Only weekly awards settle immediately.
 */
export function buildMoneyBoard(data, config, firstRound, overrides = {}) {
    // Resolvability first: you cannot judge what a config costs without knowing
    // how many times each award pays, and that is a property of its computation.
    assertResolvable(config);
    assertBalanced(config);
    // Accepts either override shape; see normalizeLedgerOverrides.
    const ruled = normalizeLedgerOverrides(overrides);
    const regWeeksDone = data.completedWeeks.filter((w) => w <= config.regularSeasonWeeks);
    const seasonOver = regWeeksDone.length >= config.regularSeasonWeeks;
    const seasonStatus = seasonOver ? 'settled' : 'live';
    // Computed whether or not this league pays a survivor pot: the board
    // publishes the elimination order either way, and the recap narrates it.
    const survivor = computeSurvivor(data);
    // An award exists in a season only if that season's config lists it — 2022
    // had no Heatbreaker, First Round Clown or Busted, and simply omits them.
    // Each rule says how it is decided; the resolver for that kind says who won.
    const awards = [];
    for (const rule of config.awards) {
        const resolve = AWARD_RESOLVERS[rule.compute.kind];
        const outcomes = resolve({
            data,
            config,
            rule,
            firstRound,
            survivor,
            completedRegularWeeks: regWeeksDone,
            seasonOver,
            seasonStatus,
            manualWinnerId: ruled.manualWinners?.[rule.id],
            ineligible: ruled.ineligible?.[rule.id] ?? [],
        });
        for (const o of outcomes) {
            awards.push({
                id: rule.id,
                label: o.label ?? rule.label,
                description: rule.description,
                amount: rule.amount,
                winnerIds: o.winnerIds,
                status: o.status,
                week: o.week,
                detail: o.detail,
            });
        }
    }
    // --- Tally per owner ---
    const earned = new Map();
    const atStake = new Map();
    for (const a of awards) {
        if (a.winnerIds.length === 0)
            continue;
        const bucket = a.status === 'settled' ? earned : atStake;
        for (const [id, share] of splitAmong(a.amount, a.winnerIds)) {
            // Accumulate in cents; a running float total reintroduces the drift the
            // split just eliminated.
            bucket.set(id, centsToDollars(dollarsToCents(bucket.get(id) ?? 0) + dollarsToCents(share)));
        }
    }
    // Side pots actually claimed — the input to the first-place remainder. An
    // award with no winner (Busted with no arrests) contributes nothing and so
    // flows through to 1st, which is the league rule.
    const sidePotsPaid = awards.reduce((sum, a) => sum + (a.winnerIds.length > 0 ? a.amount : 0), 0);
    const placements = computePlacements(config, sidePotsPaid).map((p) => ({
        place: p.place,
        label: p.label,
        amount: p.amount,
        ownerId: overrides.placements?.[p.place] ?? null,
        derived: p.kind === 'remaining',
    }));
    // Placements are recorded by hand once the bracket finishes.
    for (const p of placements) {
        if (p.ownerId) {
            earned.set(p.ownerId, centsToDollars(dollarsToCents(earned.get(p.ownerId) ?? 0) + dollarsToCents(p.amount)));
        }
    }
    const owners = data.owners.map((o) => {
        const e = earned.get(o.id) ?? 0;
        const s = atStake.get(o.id) ?? 0;
        const paid = overrides.paid?.[o.id] ?? 0;
        return {
            ownerId: o.id,
            earned: e,
            atStake: s,
            owed: config.buyIn,
            paid,
            boughtIn: overrides.boughtIn?.[o.id] === true,
            net: centsToDollars(dollarsToCents(e) - dollarsToCents(config.buyIn)),
        };
    });
    const boughtInCount = owners.filter((o) => o.boughtIn).length;
    const assigned = centsToDollars([...earned.values()].reduce((sum, v) => sum + dollarsToCents(v), 0));
    const pot = totalPot(config);
    return {
        season: data.season,
        config,
        pot,
        awards,
        placements,
        survivor,
        owners,
        sidePotsPaid,
        unawarded: centsToDollars(dollarsToCents(pot) - dollarsToCents(assigned)),
        buyInsCollected: centsToDollars(boughtInCount * dollarsToCents(config.buyIn)),
        boughtInCount,
    };
}
//# sourceMappingURL=board.js.map